import { useState, useEffect } from "react";
import { Button, Box, Container } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { DJSimpleCodeBlock } from "../../main/components/codeblock";
import {
  IProgress,
  ISessionProps,
} from "../../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@appsflyer/fe-ui-core";
import transition from "../../main/components/transition";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { amplitudeTrack_continue } from "../../services/utils";

const InstallSDKFlutter = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [latestSdkVersion, setLatestSdkVersion] = useState("6.12.2");

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    const state: ISessionProps = location.state as ISessionProps;
    setProgress(state?.progress);
  }, [location.state]);

  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        // Using GitHub API which has CORS support
        const response = await fetch(
          "https://api.github.com/repos/AppsFlyerSDK/appsflyer-flutter-plugin/releases/latest"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch latest version");
        }
        const data = await response.json();
        const tagName = data?.tag_name;
        if (tagName) {
          const version = tagName.replace(/^v/, ""); // Remove leading 'v' if present
          setLatestSdkVersion(version);
        }
      } catch (error) {
        console.error("Failed to fetch latest SDK version:", error);
      }
    };

    fetchLatestVersion();
  }, []);

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    progressArr.push({
      id: "startsdk_startsdk",
      text: "progress_startsdk_startSDK",
      order: 3,
    });
    // Initialize progress with a copy of progressArr
    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };

    amplitudeTrack_continue(location.state)

    // Navigate directly to startsdk, skipping setpermissions, referrer, and proguard
    navigate(`/startsdk/${location.state.os}/startsdk`, {
      state: progressState,
    });
  };

  const codeStep1 = `dependencies:
  flutter:
    sdk: flutter
  appsflyer_sdk: ^${latestSdkVersion}`;

  const steps = [
    {
      label: t("StartSDK_installSDK_flutter_step1_label"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <Trans
                i18nKey="StartSDK_installSDK_flutter_step1_description"
                components={{ code: <code /> }}
              />
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={codeStep1}
              showLineNumbers={true}
              language="yaml"
              highlight="4"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_installSDK_flutter_step2_label"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Run:
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={"flutter pub get"}
              showLineNumbers={false}
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  return (
    <>
      <Container maxWidth={"lg"}>
        <Box padding={3} style={{ minHeight: "50vh" }}>
          <Typography variant="h1">
            {t("startSDK_InstallSDK_flutter_title")}
          </Typography>
          

          <Stepper
            activeStep={activeStep}
            style={{ border: "none" }}
            orientation="vertical"
          >
            {steps.map(
              ({ label, component, shouldDisplayNavigationButtons }) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {component}
                    {shouldDisplayNavigationButtons && (
                      <Box sx={{ mt: 2 }}>
                        {activeStep !== 0 && (
                          <Button
                            variant="text"
                            color="primary"
                            onClick={handleBack}
                            // sx={{ mt: 2, mr: 1 }}
                          >
                            {t("general_Back")}
                          </Button>
                        )}
                        {activeStep !== steps.length - 1 && (
                          <Button
                            // sx={{ mt: 2 }}
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                          >
                            {activeStep === steps.length - 1
                              ? "Finish"
                              : t("general_Next")}
                          </Button>
                        )}
                      </Box>
                    )}
                  </StepContent>
                </Step>
              )
            )}
          </Stepper>
        </Box>
        <Box
          display={"flex"}
          width={"100%"}
          justifyContent="space-between"
          alignSelf="end"
          justifySelf="end"
          marginTop={10}
          textAlign="end"
        >
          {" "}
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            style={{ marginRight: "5px" }}
            onClick={() => {
              const progressState: ISessionProps = {
                ...location.state,
                currentPage: location.state?.currentPage - 1,
              };
              navigate(`/integrationPath`, {
                state: progressState,
                replace: true,
              });
            }}
          >
            {t("general_Back")}
          </Button>
          <Button
            variant="contained"
            size="medium"
            color="primary"
            onClick={() => handleClick()}
          >
            {t("general_Continue")}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default transition(InstallSDKFlutter);
